import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { isSuperAdmin, canManageUsers } from "../utils/permissions";
import UserMenu from "../components/UserMenu";

const DEPARTMENTS = ["Finance", "Development", "CAM", "Engineering", "Program Management", "Operations"];
const GLOBAL_ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

export default function UserManagement() {
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editProjects, setEditProjects] = useState<any[]>([]);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", department: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [newUserProjects, setNewUserProjects] = useState<any[]>([]);
  const [showNewProjectPicker, setShowNewProjectPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All roles");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"users" | "requests">("users");
  const PAGE_SIZE = 9;
  const navigate = useNavigate();

  useEffect(() => {
    if (!canManageUsers()) { navigate("/select-project"); return; }
    fetchAll();
  }, []);

  const fetchAll = () => {
    api.get("/api/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/api/projects").then(r => setProjects(r.data)).catch(() => {});
    if (isSuperAdmin()) {
      api.get("/api/access-requests").then(r => setAccessRequests(r.data)).catch(() => {});
    }
  };

  const fetchUserProjects = async (userId: string) => {
    try {
      const r = await api.get(`/api/users/${userId}/projects`);
      setEditProjects(r.data);
    } catch { setEditProjects([]); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) { setError("All fields required"); return; }
    setLoading(true); setError("");
    try {
      const res = await api.post("/api/users", form);
      const newId = res.data.id;
      if (newUserProjects.length > 0) {
        await api.put(`/api/users/${newId}/projects`, { projects: newUserProjects });
      }
      setForm({ name: "", email: "", password: "", role: "viewer", department: "" });
      setNewUserProjects([]);
      setShowAdd(false);
      fetchAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to create user");
    } finally { setLoading(false); }
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setLoading(true);
    try {
      await api.put(`/api/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
        department: editUser.department,
        is_active: editUser.is_active,
      });
      await api.put(`/api/users/${editUser.id}/projects`, { projects: editProjects });
      setEditUser(null);
      fetchAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to update");
    } finally { setLoading(false); }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try { await api.delete(`/api/users/${userId}`); fetchAll(); } catch {}
  };

  const handleToggleSuspend = async (u: any) => {
    try { await api.put(`/api/users/${u.id}`, { is_active: !u.is_active }); fetchAll(); } catch {}
  };

  const handleApproveUser = async (userId: string) => {
    try { await api.put(`/api/users/${userId}`, { is_active: true }); fetchAll(); } catch {}
  };

  const handleApproveRequest = async (reqId: string) => {
    try { await api.put(`/api/access-requests/${reqId}/approve`); fetchAll(); } catch {}
  };

  const handleRejectRequest = async (reqId: string) => {
    try { await api.put(`/api/access-requests/${reqId}/reject`); fetchAll(); } catch {}
  };

  // Edit drawer project helpers
  const unassignedProjects = projects.filter(p => !editProjects.find(ep => ep.project_id === p.id));
  const addEditProject = (p: any) => {
    setEditProjects([...editProjects, { project_id: p.id, project_name: p.name, role: "viewer" }]);
    setShowProjectPicker(false);
  };
  const removeEditProject = (projectId: string) => {
    setEditProjects(editProjects.filter(ep => ep.project_id !== projectId));
  };
  const updateEditProjectRole = (projectId: string, role: string) => {
    setEditProjects(editProjects.map(ep => ep.project_id === projectId ? { ...ep, role } : ep));
  };

  // Add user modal project helpers
  const unassignedForNew = projects.filter(p => !newUserProjects.find(np => np.project_id === p.id));
  const addNewUserProject = (p: any) => {
    setNewUserProjects([...newUserProjects, { project_id: p.id, project_name: p.name, role: "viewer" }]);
    setShowNewProjectPicker(false);
  };
  const removeNewUserProject = (projectId: string) => {
    setNewUserProjects(newUserProjects.filter(np => np.project_id !== projectId));
  };
  const updateNewUserProjectRole = (projectId: string, role: string) => {
    setNewUserProjects(newUserProjects.map(np => np.project_id === projectId ? { ...np, role } : np));
  };

  const filtered = users.filter(u => {
    const matchSearch = search === "" || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "All roles" || u.role === roleFilter.toLowerCase().replace(" ", "_");
    const matchStatus = statusFilter === "All statuses" || (statusFilter === "Active" && u.is_active) || (statusFilter === "Inactive" && !u.is_active);
    return matchSearch && matchRole && matchStatus;
  });

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const counts = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    pending: users.filter(u => !u.is_active).length,
    accessRequests: accessRequests.filter(r => r.status === "pending").length,
  };

  const avatarColor = (name: string) => {
    const colors = ["bg-blue-200 text-blue-700", "bg-green-200 text-green-700", "bg-purple-200 text-purple-700", "bg-orange-200 text-orange-700", "bg-pink-200 text-pink-700", "bg-teal-200 text-teal-700"];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const roleStyle = (r: string) => {
    if (r === "super_admin") return "bg-purple-50 text-purple-700 border border-purple-200";
    if (r === "admin") return "bg-red-50 text-red-600 border border-red-200";
    if (r === "editor") return "bg-blue-50 text-blue-600 border border-blue-200";
    return "bg-gray-100 text-gray-600 border border-gray-200";
  };

  const roleLabel = (r: string) => {
    if (r === "super_admin") return "Super Admin";
    if (r === "admin") return "Admin";
    if (r === "editor") return "Editor";
    return "Viewer";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/select-project")}>
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">C</span>
          </div>
          <div>
            <div className="text-xs text-gray-400 leading-none">COLLINS</div>
            <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => navigate("/select-project")} className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Dashboard</button>
          <button className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Report</button>
          <button className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">History</button>
          <button className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium">User Management</button>
        </div>
        <UserMenu />
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">USER MANAGEMENT</h1>
            <p className="text-xs text-gray-400 mt-0.5">Provision team members, assign roles, and manage project access.</p>
          </div>
          {isSuperAdmin() && (
            <button onClick={() => { setShowAdd(true); setError(""); setNewUserProjects([]); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              + Add New User
            </button>
          )}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "TOTAL USERS", value: counts.total, sub: "+2 this month", subColor: "text-green-500" },
            { label: "ACTIVE", value: counts.active, sub: "100% active rate", subColor: "text-green-500" },
            { label: "PENDING INVITE", value: counts.pending, sub: counts.pending > 0 ? "Awaiting activation" : "None pending", subColor: counts.pending > 0 ? "text-orange-500" : "text-gray-400" },
            { label: "ACCESS REQUESTS", value: counts.accessRequests, sub: counts.accessRequests > 0 ? "Needs review" : "All reviewed", subColor: counts.accessRequests > 0 ? "text-orange-500" : "text-gray-400" },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className={`text-xs font-medium mb-1 ${c.subColor}`}>{c.sub}</p>
              <p className="text-3xl font-bold text-gray-900">{c.value}</p>
              <p className="text-xs text-gray-400 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        {isSuperAdmin() && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => setActiveTab("users")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === "users" ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
              Users
            </button>
            <button onClick={() => setActiveTab("requests")}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${activeTab === "requests" ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
              Access Requests
              {counts.accessRequests > 0 && (
                <span className="bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{counts.accessRequests}</span>
              )}
            </button>
          </div>
        )}

        {activeTab === "users" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
                  className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                <option>All roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                <option>All statuses</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">NAME</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">EMAIL ADDRESS</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">DEPARTMENT</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">ROLE</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">STATUS</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(u => (
                    <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${avatarColor(u.name)}`}>
                            {u.name.split(" ").map((n: string) => n[0]).join("").toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{u.name}</p>
                            <p className="text-xs text-gray-400">@{u.name.toLowerCase().replace(/\s+/g, "")}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        {u.department && <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{u.department}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${roleStyle(u.role)}`}>{roleLabel(u.role)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active
                          ? <span className="flex items-center gap-1 text-xs text-green-600"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Active</span>
                          : <span className="flex items-center gap-1 text-xs text-orange-500"><span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>Pending</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {!u.is_active && isSuperAdmin() && (
                            <button onClick={() => handleApproveUser(u.id)}
                              className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200 mr-1">
                              Approve
                            </button>
                          )}
                          {/* Edit */}
                          {isSuperAdmin() && (
                            <button title="Edit user" onClick={() => { setEditUser({ ...u }); fetchUserProjects(u.id); setShowProjectPicker(false); }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {/* Suspend/Activate toggle */}
                          {isSuperAdmin() && u.email !== localStorage.getItem("email") && (
                            <button title={u.is_active ? "Suspend user" : "Activate user"}
                              onClick={() => handleToggleSuspend(u)}
                              className={`p-1.5 rounded ${u.is_active ? "text-gray-400 hover:text-amber-600 hover:bg-amber-50" : "text-amber-500 hover:text-green-600 hover:bg-green-50"}`}>
                              {u.is_active ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                          )}
                          {/* Delete */}
                          {isSuperAdmin() && u.email !== localStorage.getItem("email") && (
                            <button title="Delete user" onClick={() => handleDelete(u.id)}
                              className="p-1.5 text-red-500 hover:text-red-700 rounded hover:bg-red-50">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 py-3">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)}
                      className={`w-7 h-7 rounded text-xs ${page === i + 1 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "requests" && isSuperAdmin() && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">USER</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">PROJECT</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">REQUESTED ROLE</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">NOTE</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">STATUS</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {accessRequests.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No access requests</td></tr>
                )}
                {accessRequests.map(r => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{r.user_name}</p>
                      <p className="text-xs text-gray-400">{r.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.project_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${roleStyle(r.requested_role)}`}>{roleLabel(r.requested_role)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">{r.note || "—"}</td>
                    <td className="px-4 py-3">
                      {r.status === "pending" && <span className="text-xs text-orange-500 font-medium">⏳ Pending</span>}
                      {r.status === "approved" && <span className="text-xs text-green-600 font-medium">✓ Approved</span>}
                      {r.status === "rejected" && <span className="text-xs text-red-500 font-medium">✗ Rejected</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.status === "pending" && (
                        <div className="flex gap-2">
                          <button onClick={() => handleApproveRequest(r.id)}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">Approve</button>
                          <button onClick={() => handleRejectRequest(r.id)}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200">Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Add New User</h2>
                <p className="text-xs text-gray-400 mt-0.5">Invite a team member and assign them to a project.</p>
              </div>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
                <input placeholder="e.g. Jane Smith" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Email Address *</label>
                <input placeholder="e.g. jane@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Department *</label>
              <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                <option value="">Select Department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Password *</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} placeholder="Password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button type="button" onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Global Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                {GLOBAL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {/* WBS / Project Access */}
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-2">WBS / Project Access</label>
              <div className="space-y-2">
                {newUserProjects.map(np => (
                  <div key={np.project_id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700 flex-1 truncate">{np.project_name}</span>
                    <select value={np.role} onChange={e => updateNewUserProjectRole(np.project_id, e.target.value)}
                      className="border border-gray-200 rounded px-2 py-0.5 text-xs focus:outline-none">
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button onClick={() => removeNewUserProject(np.project_id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                  </div>
                ))}
                {/* Project picker dropdown */}
                {showNewProjectPicker ? (
                  <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                    {unassignedForNew.length === 0
                      ? <p className="text-xs text-gray-400 p-3 text-center">All projects assigned</p>
                      : unassignedForNew.map(p => (
                        <div key={p.id} onClick={() => addNewUserProject(p)}
                          className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                          {p.name}
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <button onClick={() => setShowNewProjectPicker(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                    <span className="text-base leading-none">+</span> Add another project
                  </button>
                )}
              </div>
            </div>

            {/* Send activation email — non-functional, info icon */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" disabled className="w-4 h-4 rounded opacity-40" />
              <span className="text-sm text-gray-400">Send Activation Email to User</span>
              <div className="relative group ml-1">
                <div className="w-4 h-4 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs cursor-help">i</div>
                <div className="absolute left-6 -top-1 bg-gray-800 text-white text-xs rounded px-2 py-1 w-48 hidden group-hover:block z-10 leading-tight">
                  Email notifications are not configured yet. This feature is coming soon.
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => { setShowAdd(false); setError(""); }} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} disabled={loading}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Adding..." : "Send Invite →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Drawer */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/20" onClick={() => setEditUser(null)} />
          <div className="w-80 bg-white shadow-xl flex flex-col overflow-y-auto">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${avatarColor(editUser.name)}`}>
                  {editUser.name.split(" ").map((n: string) => n[0]).join("").toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{editUser.name}</p>
                  <p className="text-xs text-gray-400">{editUser.email}</p>
                </div>
              </div>
              <button onClick={() => setEditUser(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="p-4 space-y-4 flex-1">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">STATUS</label>
                <div className="flex gap-2">
                  {["Active", "Inactive"].map(s => (
                    <button key={s} onClick={() => setEditUser({ ...editUser, is_active: s === "Active" })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${editUser.is_active === (s === "Active") ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 text-gray-600"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">DEPARTMENT</label>
                <select value={editUser.department || ""} onChange={e => setEditUser({ ...editUser, department: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                  <option value="">Select Department</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">ROLE</label>
                <select value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                  {GLOBAL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              {/* Project access — show assigned, then + Add project */}
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-2">WBS / PROJECT ACCESS</label>
                <div className="space-y-2">
                  {editProjects.map(ep => (
                    <div key={ep.project_id} className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 truncate flex-1">{ep.project_name || projects.find(p => p.id === ep.project_id)?.name}</span>
                        <button onClick={() => removeEditProject(ep.project_id)} className="text-red-400 hover:text-red-600 text-xs ml-2">✕</button>
                      </div>
                      <select value={ep.role} onChange={e => updateEditProjectRole(ep.project_id, e.target.value)}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none">
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  ))}

                  {/* Project picker */}
                  {showProjectPicker ? (
                    <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                      {unassignedProjects.length === 0
                        ? <p className="text-xs text-gray-400 p-3 text-center">All projects assigned</p>
                        : unassignedProjects.map(p => (
                          <div key={p.id} onClick={() => addEditProject(p)}
                            className="px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                            {p.name}
                          </div>
                        ))
                      }
                    </div>
                  ) : (
                    <button onClick={() => setShowProjectPicker(true)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium mt-1">
                      <span className="text-base leading-none">+</span> Add project
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2">
              <button onClick={() => setEditUser(null)} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600">Cancel</button>
              <button onClick={handleEditSave} disabled={loading} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
